import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import {
	BreadcrumbItem,
	BreadcrumbSeparator,
	DebtBreadcrumb,
	DebtContainer,
	DebtTitle,
} from "../DebtManagement/styled/DebtManagementStyles";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import {
	PricePlan,
	PricePlanApplyResult,
	PricePlanHistoryItem,
	PricePlanRollbackResult,
	PricePlanValidationError,
	applyPricePlan,
	getCurrentPricePlan,
	getPricePlanHistory,
	rollbackPricePlan,
	toPricePlanError,
	uploadPricePlan,
} from "../../../services/PricePlanService";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";
import { useIsSuperAdmin } from "../../hooks/useIsSuperAdmin";
import ConfirmTypeDialog from "./ConfirmTypeDialog";
import PlanTable from "./PlanTable";
import { BIG_CHANGE_PCT, fmtDateTime, fmtDuration, fmtPct, fmtUsd, parsePlanFileText } from "./pricePlanUtils";
import { btn, cell, chip, headCell, muted, panel, panelTitle, tone } from "./pricePlanStyles";

/**
 * PRICE PLANS — chỉ super admin (cờ is_super_admin) + quyền partners.manage.
 *
 * Luồng: file plan (.json, tính từ dữ liệu quét đối thủ) → Upload (server kiểm, lưu thành plan CHỜ)
 *        → xem bảng so sánh → "Apply price plan" (gõ APPLY) mới đổi giá thật.
 *        Mỗi lần apply có snapshot ⇒ Roll back được từ History (gõ ROLLBACK).
 * Giao diện TIẾNG ANH như cả Admin.
 */

export const PRICE_PLAN_PERMISSION = "partners.manage";

const readFileText = (file: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result || ""));
		reader.onerror = () => reject(new Error("Could not read the file."));
		reader.readAsText(file);
	});

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div style={{ padding: "60px 20px", textAlign: "center", color: "#667085" }}>{children}</div>
);

export default function PricePlans() {
	const { hasPermission, loaded: permLoaded } = useAdminPermissions();
	const { isSuperAdmin, loaded: authLoaded } = useIsSuperAdmin();

	let body: React.ReactNode;
	if (!permLoaded || !authLoaded) body = <Centered>Checking your access…</Centered>;
	else if (!isSuperAdmin || !hasPermission(PRICE_PLAN_PERMISSION))
		body = (
			<Centered>
				<div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
				<h2 style={{ color: "#1d2939", margin: "0 0 8px" }}>Super admins only</h2>
				<p style={{ margin: 0 }}>
					Price plans change live prices for every station, so only super admins can open this page.
				</p>
			</Centered>
		);
	else body = <PricePlansContent />;

	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtContainer>
					<DebtBreadcrumb>
						<BreadcrumbItem
							as="a"
							href="/"
							style={{ textDecoration: "none" }}>
							{"Home"}
						</BreadcrumbItem>
						<BreadcrumbSeparator
							src="./static/img/direction-right.png"
							alt=">"
						/>
						<BreadcrumbItem active>{"Price plans"}</BreadcrumbItem>
					</DebtBreadcrumb>
					<DebtTitle>Price plans</DebtTitle>
					{body}
				</DebtContainer>
			</OrderInner>
		</MyBookingWrapper>
	);
}

export function PricePlansContent() {
	/* ── pending plan ── */
	const [plan, setPlan] = useState<PricePlan | null>(null);
	const [planLoading, setPlanLoading] = useState(true);
	const [planError, setPlanError] = useState<string | null>(null);

	/* ── upload ── */
	const fileRef = useRef<HTMLInputElement | null>(null);
	const [uploading, setUploading] = useState(false);
	const [uploadMsg, setUploadMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
	const [uploadErrors, setUploadErrors] = useState<PricePlanValidationError[]>([]);

	/* ── history ── */
	const [history, setHistory] = useState<PricePlanHistoryItem[]>([]);
	const [historyLoading, setHistoryLoading] = useState(true);
	const [historyError, setHistoryError] = useState<string | null>(null);

	/* ── apply ── */
	const [applyOpen, setApplyOpen] = useState(false);
	const [applyBusy, setApplyBusy] = useState(false);
	const [applyError, setApplyError] = useState<string | null>(null);
	const [applyResult, setApplyResult] = useState<PricePlanApplyResult | null>(null);

	/* ── rollback ── */
	const [rbTarget, setRbTarget] = useState<PricePlanHistoryItem | null>(null);
	const [rbBusy, setRbBusy] = useState(false);
	const [rbError, setRbError] = useState<string | null>(null);
	const [rbResult, setRbResult] = useState<PricePlanRollbackResult | null>(null);

	// Khoá đồng bộ chống bấm 2 lần (state cập nhật trễ 1 nhịp render)
	const inflight = useRef(false);

	const loadPlan = useCallback(async () => {
		setPlanLoading(true);
		setPlanError(null);
		try {
			setPlan(await getCurrentPricePlan());
		} catch (e) {
			setPlanError(toPricePlanError(e).message);
		} finally {
			setPlanLoading(false);
		}
	}, []);

	const loadHistory = useCallback(async () => {
		setHistoryLoading(true);
		setHistoryError(null);
		try {
			setHistory(await getPricePlanHistory());
		} catch (e) {
			setHistoryError(toPricePlanError(e).message);
		} finally {
			setHistoryLoading(false);
		}
	}, []);

	useEffect(() => {
		loadPlan();
		loadHistory();
	}, [loadPlan, loadHistory]);

	const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files && e.target.files[0];
		// cho phép chọn lại cùng file sau khi sửa
		e.target.value = "";
		if (!file || uploading) return;
		setUploadMsg(null);
		setUploadErrors([]);
		let text = "";
		try {
			text = await readFileText(file);
		} catch (err) {
			setUploadMsg({ tone: "err", text: (err as Error).message });
			return;
		}
		const parsed = parsePlanFileText(text);
		if ("error" in parsed) {
			setUploadMsg({ tone: "err", text: `${file.name}: ${parsed.error}` });
			return;
		}
		setUploading(true);
		try {
			const stored = await uploadPricePlan(parsed.plan);
			setPlan(stored);
			setUploadMsg({
				tone: "ok",
				text: `Uploaded ${file.name}: ${
					stored?.rows?.length ?? 0
				} stations. Review below — nothing changes until you apply.`,
			});
		} catch (err) {
			const pe = toPricePlanError(err);
			setUploadMsg({ tone: "err", text: pe.message });
			setUploadErrors(pe.errors);
		} finally {
			setUploading(false);
		}
	};

	const doApply = async () => {
		if (!plan || inflight.current) return;
		inflight.current = true;
		setApplyBusy(true);
		setApplyError(null);
		try {
			const res = await applyPricePlan(plan.id, {
				hash: plan.hash,
				confirmBigChanges: (plan.summary?.bigChanges ?? 0) > 0,
			});
			setApplyResult(res);
			loadPlan();
			loadHistory();
		} catch (e) {
			setApplyError(toPricePlanError(e).message);
		} finally {
			inflight.current = false;
			setApplyBusy(false);
		}
	};

	const doRollback = async () => {
		if (!rbTarget || inflight.current) return;
		inflight.current = true;
		setRbBusy(true);
		setRbError(null);
		try {
			setRbResult(await rollbackPricePlan(rbTarget.snapshotId));
			loadPlan();
			loadHistory();
		} catch (e) {
			setRbError(toPricePlanError(e).message);
		} finally {
			inflight.current = false;
			setRbBusy(false);
		}
	};

	const openApply = () => {
		setApplyError(null);
		setApplyResult(null);
		setApplyOpen(true);
	};
	const openRollback = (item: PricePlanHistoryItem) => {
		setRbError(null);
		setRbResult(null);
		setRbTarget(item);
	};

	const s = plan?.summary;
	const bigChanges = s?.bigChanges ?? 0;

	return (
		<div style={{ maxWidth: 1400 }}>
			{/* 1. Giải thích */}
			<div style={{ ...panel, background: "#f8f9fc" }}>
				<p style={{ margin: 0, lineHeight: 1.55, color: "#344054" }}>
					Price plans are computed from competitor price scans. Upload a plan file to review the proposed
					all-in USD prices per station.{" "}
					<b>Nothing changes for customers or stations until you press “Apply price plan”.</b> Every apply
					saves a snapshot, so it can be rolled back from History.
				</p>
			</div>

			{/* 2. Upload */}
			<section style={panel}>
				<h3 style={panelTitle}>Upload plan</h3>
				<div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
					<input
						ref={fileRef}
						type="file"
						accept=".json,application/json"
						onChange={onFile}
						style={{ display: "none" }}
						data-testid="plan-file-input"
					/>
					<button
						type="button"
						disabled={uploading}
						onClick={() => fileRef.current?.click()}
						style={btn(false, uploading)}>
						{uploading ? "Uploading…" : "Choose .json file"}
					</button>
					{uploading ? <CircularProgress size={18} /> : null}
					<span style={muted}>The file may contain {"{ plan: {…} }"} or the plan object itself.</span>
				</div>
				{uploadMsg ? (
					<p
						role={uploadMsg.tone === "err" ? "alert" : "status"}
						style={{
							margin: "10px 0 0",
							fontWeight: 600,
							color: uploadMsg.tone === "ok" ? tone.ok : tone.err,
						}}>
						{uploadMsg.text}
					</p>
				) : null}
				{uploadErrors.length ? (
					<div
						style={{
							overflowX: "auto",
							marginTop: 10,
							border: `1px solid ${tone.errBorder}`,
							borderRadius: 8,
						}}>
						<table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
							<thead>
								<tr>
									<th style={{ ...headCell, background: tone.errBg, width: 120 }}>Station</th>
									<th style={{ ...headCell, background: tone.errBg }}>Error</th>
								</tr>
							</thead>
							<tbody>
								{uploadErrors.map((er, i) => (
									<tr key={`${er.partnerId}-${i}`}>
										<td style={cell}>{er.partnerId != null ? `#${er.partnerId}` : "—"}</td>
										<td style={{ ...cell, whiteSpace: "normal" }}>{er.error}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : null}
			</section>

			{/* 3. Plan đang chờ */}
			<section style={panel}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						gap: 10,
						flexWrap: "wrap",
					}}>
					<h3 style={{ ...panelTitle, margin: 0 }}>Pending plan</h3>
					{plan ? (
						<button
							type="button"
							onClick={openApply}
							disabled={applyBusy}
							style={{ ...btn(true, applyBusy), padding: "8px 16px" }}>
							Apply price plan
						</button>
					) : null}
				</div>
				{planLoading ? (
					<div style={{ padding: 20, textAlign: "center" }}>
						<CircularProgress size={24} />
					</div>
				) : planError ? (
					<p
						role="alert"
						style={{ color: tone.err, fontWeight: 600 }}>
						Could not load the pending plan: {planError}{" "}
						<button
							type="button"
							onClick={loadPlan}
							style={btn()}>
							Retry
						</button>
					</p>
				) : !plan ? (
					<p style={{ ...muted, margin: "10px 0 0" }}>
						No pending plan. Upload a plan file to review it here.
					</p>
				) : (
					<div style={{ marginTop: 12 }}>
						<div style={{ ...muted, marginBottom: 10 }}>
							Plan <b style={{ color: "#344054" }}>#{String(plan.id)}</b> · created{" "}
							{fmtDateTime(plan.createdAt)}
							{plan.createdBy != null ? ` by ${plan.createdBy}` : ""} · hash{" "}
							<code style={{ fontSize: 12 }}>{String(plan.hash || "").slice(0, 12)}</code>
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
							<span style={chip("#f2f4f7", "#344054")}>
								Config A-B-C: {plan.config?.A}-{plan.config?.B}-{plan.config?.C}
							</span>
							<span style={chip("#f2f4f7", "#344054")}>
								Protection: {fmtUsd(plan.config?.protection)}
							</span>
							{plan.config?.capStandard != null ? (
								<span style={chip("#f2f4f7", "#344054")}>
									Cap standard: {fmtUsd(plan.config.capStandard)}
								</span>
							) : null}
							{plan.config?.capLarge != null ? (
								<span style={chip("#f2f4f7", "#344054")}>
									Cap large: {fmtUsd(plan.config.capLarge)}
								</span>
							) : null}
						</div>
						<div
							style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
							data-testid="plan-summary">
							<span style={chip("#eef4ff", "#3538cd")}>Stations: {s?.stations ?? 0}</span>
							<span style={chip("#ecfdf3", "#027a48")}>
								Up: {s?.up ?? 0}
								{s?.maxRisePct != null ? ` (max ${fmtPct(s.maxRisePct)})` : ""}
							</span>
							<span style={chip("#fffaeb", "#b54708")}>
								Down: {s?.down ?? 0}
								{s?.maxDropPct != null ? ` (max ${fmtPct(-Math.abs(s.maxDropPct))})` : ""}
							</span>
							<span style={chip("#f2f4f7", "#475467")}>Same: {s?.same ?? 0}</span>
							<span style={bigChanges > 0 ? chip("#fef3f2", "#b42318") : chip("#f2f4f7", "#475467")}>
								Big changes (&gt;{BIG_CHANGE_PCT}%): {bigChanges}
							</span>
						</div>
						{plan.warnings && plan.warnings.length ? (
							<div
								style={{
									background: tone.warnBg,
									border: `1px solid ${tone.warnBorder}`,
									color: tone.warnFg,
									borderRadius: 8,
									padding: "10px 14px",
									marginBottom: 12,
								}}
								data-testid="plan-warnings">
								<b>Warnings ({plan.warnings.length})</b>
								<ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
									{plan.warnings.map((w, i) => (
										<li key={i}>{w}</li>
									))}
								</ul>
							</div>
						) : null}
						<PlanTable rows={plan.rows || []} />
					</div>
				)}
			</section>

			{/* 5. Lịch sử */}
			<section style={panel}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
					<h3 style={{ ...panelTitle, margin: 0 }}>History</h3>
					<button
						type="button"
						onClick={loadHistory}
						disabled={historyLoading}
						style={btn(false, historyLoading)}>
						Refresh
					</button>
				</div>
				{historyLoading ? (
					<div style={{ padding: 20, textAlign: "center" }}>
						<CircularProgress size={24} />
					</div>
				) : historyError ? (
					<p
						role="alert"
						style={{ color: tone.err, fontWeight: 600 }}>
						Could not load history: {historyError}
					</p>
				) : history.length === 0 ? (
					<p style={{ ...muted, margin: "10px 0 0" }}>No price plan has been applied yet.</p>
				) : (
					<div style={{ overflowX: "auto", marginTop: 10, border: "1px solid #eaecf0", borderRadius: 8 }}>
						<table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640, fontSize: 13 }}>
							<thead>
								<tr>
									<th style={headCell}>When</th>
									<th style={headCell}>Action</th>
									<th style={headCell}>Plan</th>
									<th style={headCell}>By</th>
									<th style={{ ...headCell, textAlign: "right" }}>Stations</th>
									<th style={headCell}>Snapshot</th>
									<th style={headCell} />
								</tr>
							</thead>
							<tbody>
								{history.map((h) => (
									<tr
										key={String(h.snapshotId)}
										data-testid={`history-row-${h.snapshotId}`}>
										<td style={cell}>{fmtDateTime(h.appliedAt)}</td>
										<td style={cell}>
											{h.kind === "rollback" ? (
												<span style={chip("#fffaeb", "#b54708")}>Rollback</span>
											) : (
												<span style={chip("#eff8ff", "#175cd3")}>Apply</span>
											)}
											{/* Lần áp / quay lui hỏng (đã tự trả về) hoặc đang chạy ⇒ không có gì để quay lui */}
											{h.status && h.status !== "applied" ? (
												<span style={{ ...chip("#fef3f2", "#b42318"), marginLeft: 6 }}>
													{h.status === "applying"
														? "Running"
														: h.status === "failed_reverted"
														? "Failed — reverted"
														: "Failed — check stations"}
												</span>
											) : null}
										</td>
										<td style={cell}>{h.planId != null ? `#${h.planId}` : "—"}</td>
										<td style={cell}>{h.appliedBy != null ? String(h.appliedBy) : "—"}</td>
										<td style={{ ...cell, textAlign: "right" }}>{h.count}</td>
										<td style={cell}>#{String(h.snapshotId)}</td>
										<td style={{ ...cell, textAlign: "right" }}>
											{!h.status || h.status === "applied" ? (
												<button
													type="button"
													onClick={() => openRollback(h)}
													disabled={rbBusy}
													style={btn(false, rbBusy)}>
													Roll back
												</button>
											) : null}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{/* 4. Xác nhận Apply */}
			<ConfirmTypeDialog
				open={applyOpen && !!plan}
				title="Apply price plan"
				word="APPLY"
				confirmLabel="Apply price plan"
				checkboxes={
					bigChanges > 0
						? [{ id: "big", label: `I confirm ${bigChanges} stations change more than ${BIG_CHANGE_PCT}%` }]
						: []
				}
				busy={applyBusy}
				error={applyError}
				result={
					applyResult ? (
						<p style={{ margin: 0, color: tone.ok, fontWeight: 600 }}>
							Applied new prices to {applyResult.applied} stations in{" "}
							{fmtDuration(applyResult.durationMs)}. Snapshot #{String(applyResult.snapshotId)} saved —
							you can roll back from History.
						</p>
					) : null
				}
				onConfirm={doApply}
				onClose={() => setApplyOpen(false)}>
				{plan ? (
					<div style={{ lineHeight: 1.6 }}>
						<p style={{ margin: "0 0 6px" }}>
							This will change live prices for <b>{s?.stations ?? plan.rows?.length ?? 0} stations</b>:
						</p>
						<ul style={{ margin: 0, paddingLeft: 20 }}>
							<li>{s?.up ?? 0} go up</li>
							<li>{s?.down ?? 0} go down</li>
							<li>{s?.same ?? 0} stay the same</li>
							<li style={bigChanges > 0 ? { color: tone.err, fontWeight: 600 } : undefined}>
								{bigChanges} change more than {BIG_CHANGE_PCT}%
							</li>
						</ul>
						{plan.warnings?.length ? (
							<p style={{ margin: "8px 0 0", color: tone.warnFg }}>
								{plan.warnings.length} warning(s) — see the plan panel.
							</p>
						) : null}
					</div>
				) : null}
			</ConfirmTypeDialog>

			{/* 5b. Xác nhận Roll back */}
			<ConfirmTypeDialog
				open={!!rbTarget}
				title="Roll back prices"
				word="ROLLBACK"
				confirmLabel="Roll back"
				busy={rbBusy}
				error={rbError}
				result={
					rbResult ? (
						<p style={{ margin: 0, color: tone.ok, fontWeight: 600 }}>
							Rolled back {rbResult.applied} stations. New snapshot #{String(rbResult.snapshotId)} saved.
						</p>
					) : null
				}
				onConfirm={doRollback}
				onClose={() => setRbTarget(null)}>
				{rbTarget ? (
					<p style={{ margin: 0, lineHeight: 1.6 }}>
						Restore the prices saved in snapshot <b>#{String(rbTarget.snapshotId)}</b> (
						{rbTarget.kind === "rollback" ? "rollback" : "apply"} on {fmtDateTime(rbTarget.appliedAt)},{" "}
						{rbTarget.count} stations). Live prices for these stations will change immediately.
					</p>
				) : null}
			</ConfirmTypeDialog>
		</div>
	);
}
